import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  CURRENCY_META,
  SUPPORTED_CURRENCIES,
  fetchFxRates,
  selectFxCachedAt,
  selectFxRates,
  selectFxRatesError,
  selectFxRatesLoading,
  selectFxSource,
  selectFxTtlMs,
  selectPreferredCurrency,
  setPreferredCurrency,
  type SupportedCurrency,
} from '../store/slices/currencySlice';

export type CurrencyFormatOptions = {
  compact?: boolean;
  decimals?: number;
};

type CurrencyContextValue = {
  displayCurrency: SupportedCurrency;
  setDisplayCurrency: (currency: SupportedCurrency) => void;
  fxRates: Record<string, number>;
  fxRatesLoading: boolean;
  fxRatesError: string | null;
  fxMeta: {
    cachedAt: string | null;
    ttlMs: number;
    source: 'redis' | 'api' | 'fallback' | null;
  };
  refreshFxRates: (force?: boolean) => Promise<void>;
  getRate: (currency?: SupportedCurrency) => number;
  convertFromUsd: (usdAmount: number, currency?: SupportedCurrency) => number;
  formatValue: (usdAmount: number, options?: CurrencyFormatOptions) => string;
  formatNumber: (usdAmount: number, options?: CurrencyFormatOptions) => string;
  formatValueForCurrency: (
    usdAmount: number,
    currency: SupportedCurrency,
    options?: CurrencyFormatOptions
  ) => string;
  formatNumberForCurrency: (
    usdAmount: number,
    currency: SupportedCurrency,
    options?: CurrencyFormatOptions
  ) => string;
};

const APP_LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  ja: 'ja-JP',
  es: 'es-ES',
  de: 'de-DE',
  fr: 'fr-FR',
};

const ZERO_DECIMAL_CURRENCIES = new Set<SupportedCurrency>(['JPY', 'KRW']);

function normaliseCurrency(value: unknown): SupportedCurrency | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalised = value.trim().toUpperCase();
  return SUPPORTED_CURRENCIES.includes(normalised as SupportedCurrency)
    ? (normalised as SupportedCurrency)
    : null;
}

function resolveLocale(currency: SupportedCurrency, appLocale?: string): string {
  if (appLocale && APP_LOCALE_MAP[appLocale]) {
    return APP_LOCALE_MAP[appLocale];
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return CURRENCY_META[currency].locale;
}

function getFractionDigits(currency: SupportedCurrency, options?: CurrencyFormatOptions) {
  if (typeof options?.decimals === 'number') {
    return options.decimals;
  }

  return ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2;
}

export function formatUsdInCurrency(
  usdAmount: number,
  currency: SupportedCurrency,
  rates: Record<string, number>,
  appLocale?: string,
  options: CurrencyFormatOptions = {}
): string {
  const converted = usdAmount * (rates[currency] ?? 1);
  const decimals = getFractionDigits(currency, options);

  return new Intl.NumberFormat(resolveLocale(currency, appLocale), {
    style: 'currency',
    currency,
    notation: options.compact ? 'compact' : 'standard',
    minimumFractionDigits: options.compact ? 0 : decimals,
    maximumFractionDigits: options.compact ? Math.min(decimals, 1) : decimals,
  }).format(converted);
}

export function formatUsdNumberInCurrency(
  usdAmount: number,
  currency: SupportedCurrency,
  rates: Record<string, number>,
  appLocale?: string,
  options: CurrencyFormatOptions = {}
): string {
  const converted = usdAmount * (rates[currency] ?? 1);
  const decimals = getFractionDigits(currency, options);

  return new Intl.NumberFormat(resolveLocale(currency, appLocale), {
    notation: options.compact ? 'compact' : 'standard',
    minimumFractionDigits: options.compact ? 0 : decimals,
    maximumFractionDigits: options.compact ? Math.min(decimals, 1) : decimals,
  }).format(converted);
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const displayCurrency = useAppSelector(selectPreferredCurrency);
  const fxRates = useAppSelector(selectFxRates);
  const fxRatesLoading = useAppSelector(selectFxRatesLoading);
  const fxRatesError = useAppSelector(selectFxRatesError);
  const cachedAt = useAppSelector(selectFxCachedAt);
  const ttlMs = useAppSelector(selectFxTtlMs);
  const source = useAppSelector(selectFxSource);

  useEffect(() => {
    void dispatch(fetchFxRates());
  }, [dispatch]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void dispatch(fetchFxRates({ force: true }));
    }, ttlMs);

    return () => window.clearInterval(interval);
  }, [dispatch, ttlMs]);

  useEffect(() => {
    const persistedCurrency = normaliseCurrency(user?.displayCurrency ?? user?.preferredCurrency);
    if (persistedCurrency && persistedCurrency !== displayCurrency) {
      dispatch(setPreferredCurrency(persistedCurrency));
    }
  }, [dispatch, displayCurrency, user?.displayCurrency, user?.preferredCurrency]);

  const setDisplayCurrency = useCallback(
    (currency: SupportedCurrency) => {
      dispatch(setPreferredCurrency(currency));
    },
    [dispatch]
  );

  const getRate = useCallback(
    (currency: SupportedCurrency = displayCurrency) => fxRates[currency] ?? 1,
    [displayCurrency, fxRates]
  );

  const convertFromUsd = useCallback(
    (usdAmount: number, currency: SupportedCurrency = displayCurrency) => usdAmount * getRate(currency),
    [displayCurrency, getRate]
  );

  const formatValueForCurrency = useCallback(
    (usdAmount: number, currency: SupportedCurrency, options?: CurrencyFormatOptions) =>
      formatUsdInCurrency(usdAmount, currency, fxRates, user?.locale, options),
    [fxRates, user?.locale]
  );

  const formatNumberForCurrency = useCallback(
    (usdAmount: number, currency: SupportedCurrency, options?: CurrencyFormatOptions) =>
      formatUsdNumberInCurrency(usdAmount, currency, fxRates, user?.locale, options),
    [fxRates, user?.locale]
  );

  const formatValue = useCallback(
    (usdAmount: number, options?: CurrencyFormatOptions) =>
      formatValueForCurrency(usdAmount, displayCurrency, options),
    [displayCurrency, formatValueForCurrency]
  );

  const formatNumber = useCallback(
    (usdAmount: number, options?: CurrencyFormatOptions) =>
      formatNumberForCurrency(usdAmount, displayCurrency, options),
    [displayCurrency, formatNumberForCurrency]
  );

  const refreshFxRates = useCallback(
    async (force = false) => {
      await dispatch(fetchFxRates(force ? { force: true } : undefined));
    },
    [dispatch]
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      displayCurrency,
      setDisplayCurrency,
      fxRates,
      fxRatesLoading,
      fxRatesError,
      fxMeta: {
        cachedAt,
        ttlMs,
        source,
      },
      refreshFxRates,
      getRate,
      convertFromUsd,
      formatValue,
      formatNumber,
      formatValueForCurrency,
      formatNumberForCurrency,
    }),
    [
      cachedAt,
      convertFromUsd,
      displayCurrency,
      formatNumber,
      formatNumberForCurrency,
      formatValue,
      formatValueForCurrency,
      fxRates,
      fxRatesError,
      fxRatesLoading,
      getRate,
      refreshFxRates,
      setDisplayCurrency,
      source,
      ttlMs,
    ]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrencyContext() {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error('useCurrencyContext must be used within a CurrencyProvider');
  }

  return context;
}
